/*
 * documentos.js — Tipos de documento y lógica pura de los archivos del cliente (C-015).
 *
 * La misma lista de tipos vive en backend/documentos.js. Los archivos de antes están guardados
 * como `general` y se muestran como "Otro".
 */
import { normalizarNombre } from './estados';

export const GRUPOS_DOCUMENTO = [
  {
    nombre: 'Del cliente',
    tipos: [
      { clave: 'estados_cuenta', nombre: 'Estados de cuenta bancarios' },
      { clave: 'facturas_ventas', nombre: 'Facturas de ventas' },
      { clave: 'facturas_gastos', nombre: 'Facturas de gastos' },
      { clave: 'cfdi_emitidos', nombre: 'CFDI emitidos' },
      { clave: 'cfdi_recibidos', nombre: 'CFDI recibidos' },
      { clave: 'nomina', nombre: 'Comprobantes de nómina' },
    ],
  },
  {
    nombre: 'Del despacho',
    tipos: [
      { clave: 'calculo_impuestos', nombre: 'Cálculo de impuestos' },
      { clave: 'declaraciones', nombre: 'Declaraciones provisionales' },
      { clave: 'pagos_impuestos', nombre: 'Pagos de impuestos' },
      { clave: 'opinion', nombre: 'Opinión de cumplimiento' },
    ],
  },
  { nombre: 'Otros', tipos: [{ clave: 'otro', nombre: 'Otro' }] },
];

export const TIPOS_DOCUMENTO = GRUPOS_DOCUMENTO.flatMap(g => g.tipos);

/** Clave del tipo de un archivo; lo que no es un tipo conocido (`general`, viejo) es "otro". */
export const tipoDeArchivo = (archivo) => {
  const clave = archivo && archivo.tipo_documento;
  return TIPOS_DOCUMENTO.some(t => t.clave === clave) ? clave : 'otro';
};

export const nombreTipo = (clave) =>
  (TIPOS_DOCUMENTO.find(t => t.clave === clave) || { nombre: 'Otro' }).nombre;

// Pistas del nombre del archivo, en orden: la primera que coincide gana. Se puede ampliar.
const PISTAS = [
  [/banco|bbva|banorte|santander|banamex|citibanamex|hsbc|scotiabank|inbursa|banregio|banbajio|afirme|estado.?de.?cuenta|edo.?(de.?)?cta/, 'estados_cuenta'],
  [/linea.?de.?captura|pago.?(de.?)?imp/, 'pagos_impuestos'],
  [/acuse|declaracion/, 'declaraciones'],
  [/opinion/, 'opinion'],
  [/calculo/, 'calculo_impuestos'],
  [/nomina/, 'nomina'],
  [/emitid/, 'cfdi_emitidos'],
  [/recibid/, 'cfdi_recibidos'],
  [/venta|ingreso/, 'facturas_ventas'],
  [/gasto|egreso|compra/, 'facturas_gastos'],
];

/** Tipo sugerido por el nombre del archivo, o null si no hay pista. Nunca sugiere "otro". */
export const sugerirTipo = (nombreArchivo) => {
  const n = normalizarNombre(nombreArchivo);
  const pista = PISTAS.find(([re]) => re.test(n));
  return pista ? pista[1] : null;
};

/** PDF e imágenes se abren en una pestaña; lo demás (Excel, Word, XML, ZIP) se descarga. */
export const seAbreEnNavegador = (nombreArchivo) =>
  /\.(pdf|png|jpe?g|gif|webp)$/i.test(nombreArchivo || '');

/** Solo los tipos que tienen archivos, en el orden del catálogo: [{ clave, nombre, archivos }]. */
export const agruparPorTipo = (archivos) =>
  TIPOS_DOCUMENTO
    .map(t => ({ ...t, archivos: (archivos || []).filter(a => tipoDeArchivo(a) === t.clave) }))
    .filter(g => g.archivos.length > 0);

export const textoArchivos = (n) =>
  n > 0 ? `${n} ${n === 1 ? 'archivo' : 'archivos'}` : 'Sin archivos';

/** Filtro de la ficha por año: tipo ('todos' o clave) y búsqueda por nombre de archivo. */
export const filtrarArchivos = (archivos, { tipo = 'todos', busqueda = '' } = {}) => {
  const q = normalizarNombre(busqueda.trim());
  return (archivos || []).filter(a =>
    (tipo === 'todos' || tipoDeArchivo(a) === tipo) &&
    (!q || normalizarNombre(a.nombre_archivo).includes(q))
  );
};

export const TAMANO_MAXIMO = 15 * 1024 * 1024;
